import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { Student, CreateStudentRequest, UpdateStudentRequest } from '../types'
import * as studentService from '../services/students'

interface StudentsState {
  // State
  students: Student[]
  loading: boolean
  error: string | null
  isCreating: boolean
  isUpdating: boolean
  isImporting: boolean

  // Actions
  fetchStudents: () => Promise<void>
  addStudent: (studentData: CreateStudentRequest) => Promise<Student>
  bulkImportStudents: (students: CreateStudentRequest[]) => Promise<{
    successful: number
    duplicates: number
    failed: number
    errors: any[]
  }>
  editStudent: (
    id: string,
    studentData: UpdateStudentRequest,
  ) => Promise<Student>
  removeStudent: (id: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  students: [],
  loading: false,
  error: null,
  isCreating: false,
  isUpdating: false,
  isImporting: false,
}

export const useStudentsStore = create<StudentsState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        fetchStudents: async () => {
          try {
            set({ loading: true, error: null })
            const response = await studentService.getStudents()
            set({
              students: response.data,
              loading: false,
            })
          } catch (error: any) {
            console.error('Error fetching students:', error)
            set({
              error: 'Failed to fetch students',
              loading: false,
            })
          }
        },

        addStudent: async (studentData: CreateStudentRequest) => {
          try {
            set({ isCreating: true, error: null })
            const response = await studentService.createStudent(studentData)
            set({ isCreating: false })
            // Reload students after successful creation
            await get().fetchStudents()
            return response.data
          } catch (error: any) {
            console.error('Error creating student:', error)
            set({
              error:
                error.response?.data?.message || 'Failed to create student',
              isCreating: false,
            })
            throw error
          }
        },

        bulkImportStudents: async (students: CreateStudentRequest[]) => {
          try {
            set({ isImporting: true, error: null })
            const response = await studentService.bulkImportStudents(students)
            set({ isImporting: false })
            // Reload students after successful import
            await get().fetchStudents()
            return response.data
          } catch (error: any) {
            console.error('Error importing students:', error)
            set({
              error:
                error.response?.data?.message || 'Failed to import students',
              isImporting: false,
            })
            throw error
          }
        },

        editStudent: async (id: string, studentData: UpdateStudentRequest) => {
          try {
            set({ isUpdating: true, error: null })
            const response = await studentService.updateStudent(id, studentData)
            set({ isUpdating: false })
            // Reload students after successful update
            await get().fetchStudents()
            return response.data
          } catch (error: any) {
            console.error('Error updating student:', error)
            set({
              error:
                error.response?.data?.message || 'Failed to update student',
              isUpdating: false,
            })
            throw error
          }
        },

        removeStudent: async (id: string) => {
          try {
            set({ loading: true, error: null })
            await studentService.deleteStudent(id)
            set({ loading: false })
          } catch (error: any) {
            console.error('Error deleting student:', error)
            set({
              error:
                error.response?.data?.message || 'Failed to delete student',
              loading: false,
            })
            throw error
          }
        },

        clearError: () => {
          set({ error: null })
        },

        reset: () => {
          set(initialState)
        },
      }),
      {
        name: 'students-store',
      },
    ),
  ),
)
